import { customEvent } from "goi_common";

import { GOI_PHONE, GOI_PHONE_HREF } from "@/constants/contact";
import { toManwon, toManwonRange } from "@/utils/formatMoney";
import { RegionCrematorium } from "@/utils/regionFacilities";

interface RegionTotalCostCTAProps {
  sigunguName: string;
  familyCostMin: number | null;
  familyCostMax: number | null;
  insideCrematorium: RegionCrematorium | null;
  burialMinCharge: number | null;
  href: string;
  /** 2026 추석. 장지, 화장장 포커스에서는 상담 전화를 빼고 비용 CTA만 남긴다. */
  hideCall?: boolean;
}

/**
 * 장례 전체 비용 리드 CTA.
 *
 * 위 세 섹션(장례식장, 화장장, 장지)의 숫자를 합산 관점으로 다시 보여주는 것이
 * 이 CTA의 설득 재료다. www는 이 정보가 3개 페이지로 흩어져 있다.
 */
export const RegionTotalCostCTA = ({
  sigunguName,
  familyCostMin,
  familyCostMax,
  insideCrematorium,
  burialMinCharge,
  href,
  hideCall = false,
}: RegionTotalCostCTAProps) => {
  const facilityRange = toManwonRange(familyCostMin, familyCostMax);

  return (
    <section className="rg-cta" aria-labelledby="rg-cta-title">
      <div className="rg-cta__body">
        <h2 id="rg-cta-title" className="rg-cta__title">
          {`${sigunguName} 장례식장, 화장, 장지를 합치면 `}
          <span className="rg-accent">전체 비용은 얼마일까요</span>
        </h2>

        <p className="rg-cta__lede">
          {`${sigunguName}에서 가족장을 치를 경우의 시설 비용입니다. 장례지도사 서비스와 장례용품 비용은 선택한 구성에 따라 달라지므로, 상담에서 항목별로 확인하실 수 있습니다.`}
        </p>

        <dl className="rg-cta__figures">
          {facilityRange && (
            <div className="rg-cta__figure">
              <dt>장례식장 가족장 예상</dt>
              <dd>{facilityRange}</dd>
            </div>
          )}

          {insideCrematorium && (
            <div className="rg-cta__figure">
              <dt>화장 요금</dt>
              <dd className="rg-accent">{insideCrematorium.appliedPrice}</dd>
            </div>
          )}

          {burialMinCharge !== null && (
            <div className="rg-cta__figure">
              <dt>장지 최저 분양가</dt>
              <dd>{toManwon(burialMinCharge)}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="rg-cta__actions">
        <a
          className="rg-btn rg-btn--primary rg-btn--lg"
          href={href}
          onClick={() =>
            customEvent({
              newGtm: {
                path: "prlr_region",
                trigger_and_target: "btn_total_cost",
              },
            })
          }
        >
          장례 전체 비용 알아보기
        </a>

        {!hideCall && (
          <a
            className="rg-cta__call"
            href={GOI_PHONE_HREF}
            onClick={() =>
              customEvent({
                newGtm: { path: "prlr_region", trigger_and_target: "btn_call" },
              })
            }
          >
            {`24시간 장례 상담 ${GOI_PHONE}`}
          </a>
        )}
      </div>
    </section>
  );
};
